import React, { useState } from 'react';
import { Building2, ExternalLink, AlertTriangle } from 'lucide-react';
import { Button, Input, Textarea } from '../../ui-v2';
import { ToggleGroup, ToggleItem } from '../../ui-v2.5';

/**
 * Rediseño (Fases 2 y 3): `parte` pinta solo un trozo:
 *   'ambito' (solo el selector) · 'informacion' (ámbito, título y descripción internos) ·
 *   'visible' (lo que ve el usuario) · 'reglas' (asignación, cancelaciones y antelación) ·
 *   un campo suelto: 'titulo', 'descripcion', 'titulo_usuario', 'descripcion_usuario' (para la
 *   rejilla por filas de BloquesCampana, que alinea interno y usuario fila a fila).
 *   Sin `parte` se pinta todo en una columna.
 * `sinAmbito`: no pinta el selector de ámbito (Editar: no se cambia; lo dice la insignia de la cabecera).
 * `autoFocusTitulo`: foco en el título al montar (por defecto, solo sin `parte`).
 */
export default function Paso1Info({
  payload, errores, dispatch, parte = null, sinAmbito = false, autoFocusTitulo = null,
}) {
  const errorDe = (campo) => errores.find((e) => e.campo === campo)?.mensaje;
  const [confirmandoCambio, setConfirmandoCambio] = useState(null);
  const ver = (p) => !parte || parte === p;

  // Cambio de ámbito: si hay técnicos o usuarios ya elegidos, pedir confirmación
  // antes de limpiarlos (porque son IDs de otra tabla).
  function intentarCambioAmbito(nuevo) {
    if (sinAmbito || nuevo === payload.ambito) return;
    const hayPersonas =
      (payload.tecnicos_ids?.length || 0) +
      (payload.usuarios_ids?.length || 0) > 0;
    if (hayPersonas) {
      setConfirmandoCambio(nuevo);
    } else {
      dispatch({ type: 'CAMBIAR_AMBITO', ambito: nuevo });
    }
  }

  function confirmarCambio() {
    dispatch({ type: 'CAMBIAR_AMBITO', ambito: confirmandoCambio });
    setConfirmandoCambio(null);
  }

  // Los dos campos de «lo que ve la persona», por separado (la rejilla por filas los pide sueltos).
  const campoTituloUsuario = (
    <Campo
      label="Título para el usuario"
      hint="Vacío → se mostrará el título interno."
    >
      <Input
        value={payload.titulo_usuario ?? ''}
        onChange={(e) =>
          dispatch({ type: 'SET_FIELD', field: 'titulo_usuario', value: e.target.value })
        }
        placeholder="Ej. «Intervención técnica programada»"
        maxLength={255}
      />
    </Campo>
  );
  const campoDescripcionUsuario = (
    <Campo
      label="Descripción para el usuario"
      hint="Vacío → se mostrará la descripción interna."
    >
      <Textarea
        value={payload.descripcion_usuario ?? ''}
        onChange={(e) =>
          dispatch({ type: 'SET_FIELD', field: 'descripcion_usuario', value: e.target.value })
        }
        placeholder="Texto orientado al usuario final (qué es, qué debe hacer, fecha límite…)"
        rows={3}
      />
    </Campo>
  );
  const verTitulo = ver('informacion') || parte === 'titulo';
  const verDescripcion = ver('informacion') || parte === 'descripcion';
  const verAmbito = !sinAmbito && (!parte || parte === 'ambito' || parte === 'informacion');

  return (
    <div className={parte ? 'space-y-5' : 'space-y-5 max-w-2xl'}>
      {(verAmbito || verTitulo || verDescripcion) && (<>
      {/* Selector de ámbito (solo en el asistente) */}
      {verAmbito && (
      <Campo
        label="Ámbito de la campaña *"
        error={errorDe('ambito')}
        hint="Define a qué colectivo van dirigidas las intervenciones. No se puede mezclar."
      >
        <div role="radiogroup" aria-label="Ámbito de la campaña" className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          <TarjetaAmbito
            icon={<Building2 className="w-4 h-4" />}
            label="Internos"
            descripcion="Empleados de la empresa"
            activo={payload.ambito === 'interno'}
            onClick={() => intentarCambioAmbito('interno')}
          />
          <TarjetaAmbito
            icon={<ExternalLink className="w-4 h-4" />}
            label="Colaboradores"
            descripcion="Personas externas"
            activo={payload.ambito === 'colaborador'}
            onClick={() => intentarCambioAmbito('colaborador')}
          />
        </div>
      </Campo>
      )}

      {verTitulo && (
      <Campo label="Título *" error={errorDe('titulo')} hint="Ej. «Mantenimiento equipos Q1»">
        <Input
          value={payload.titulo}
          onChange={(e) =>
            dispatch({ type: 'SET_FIELD', field: 'titulo', value: e.target.value })
          }
          placeholder="Nombre identificativo de la campaña"
          maxLength={255}
          autoFocus={autoFocusTitulo ?? !parte}
        />
      </Campo>
      )}

      {verDescripcion && (
      <Campo label="Descripción" error={errorDe('descripcion')}>
        <Textarea
          value={payload.descripcion || ''}
          onChange={(e) =>
            dispatch({ type: 'SET_FIELD', field: 'descripcion', value: e.target.value })
          }
          placeholder="Detalles para técnicos y personas convocadas"
          rows={3}
        />
      </Campo>
      )}
      </>)}

      {parte === 'titulo_usuario' && campoTituloUsuario}
      {parte === 'descripcion_usuario' && campoDescripcionUsuario}

      {/* ─── Fase 15 (#5) · Información visible para el usuario final ─── */}
      {ver('visible') && (
      <div className={parte ? '' : 'mt-6 pt-6 border-t-2 border-dashed border-slate-200'}>
        {parte ? (
          <p className="text-xs text-slate-500 mb-4">
            Déjalo vacío para usar el título y la descripción internos.
          </p>
        ) : (
        <div className="mb-4">
          <h3 className="text-sm font-semibold text-slate-900">
            Información visible para el usuario
          </h3>
          <p className="text-xs text-slate-500 mt-1">
            Contenido que verá el usuario final (en Mis Citas y en las
            notificaciones). Déjalo vacío para usar el título y la descripción
            internos.
          </p>
        </div>
        )}

        <div className="space-y-5">
          {campoTituloUsuario}
          {campoDescripcionUsuario}
        </div>
      </div>
      )}

      {ver('reglas') && (
      <div className={parte ? 'grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5' : 'grid grid-cols-1 sm:grid-cols-2 gap-5'}>
        <Campo
          label="Asignación de técnico"
          hint="«Libre»: la persona elige. «Automática»: el sistema reparte."
        >
          {/* Control segmentado del DS v2.5 (antes, dos pastillas negras). */}
          <ToggleGroup
            value={payload.asignacion_modo}
            onChange={(v) => dispatch({ type: 'SET_FIELD', field: 'asignacion_modo', value: v })}
          >
            <ToggleItem value="libre" label="Libre" />
            <ToggleItem value="automatica" label="Automática" />
          </ToggleGroup>
        </Campo>

        <Campo
          label="Máx. cancelaciones por persona"
          error={errorDe('max_cancelaciones_default')}
          hint="Veces que cada persona puede cancelar su cita en esta campaña."
        >
          <Input
            type="number"
            min={0}
            max={50}
            value={payload.max_cancelaciones_default}
            onChange={(e) =>
              dispatch({
                type: 'SET_FIELD',
                field: 'max_cancelaciones_default',
                value: parseInt(e.target.value, 10) || 0,
              })
            }
            className="w-28"
          />
        </Campo>

        <Campo
          label="Antelación mínima para cancelar (horas)"
          error={errorDe('horas_antelacion_cancelar')}
          hint="0 = sin restricción. P. ej. 24 = solo se puede cancelar hasta 24h antes."
        >
          <Input
            type="number"
            min={0}
            max={720}
            value={payload.horas_antelacion_cancelar || 0}
            onChange={(e) =>
              dispatch({
                type: 'SET_FIELD',
                field: 'horas_antelacion_cancelar',
                value: parseInt(e.target.value, 10) || 0,
              })
            }
            className="w-28"
          />
        </Campo>
      </div>
      )}

      {/* Modal de confirmación de cambio de ámbito */}
      {confirmandoCambio && (
        <ConfirmacionCambioAmbito
          ambitoActual={payload.ambito}
          ambitoNuevo={confirmandoCambio}
          cantidadAfectada={
            (payload.tecnicos_ids?.length || 0) + (payload.usuarios_ids?.length || 0)
          }
          onConfirmar={confirmarCambio}
          onCancelar={() => setConfirmandoCambio(null)}
        />
      )}
    </div>
  );
}

// Rediseño (Fase 2): tarjeta seleccionable del DS v2.5 (borde y acento de marca), sin fondo negro.
function TarjetaAmbito({ icon, label, descripcion, activo, onClick }) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={activo}
      onClick={onClick}
      className={
        'relative text-left rounded-v25-md border p-3 flex items-start gap-3 transition-colors ' +
        'focus-visible:outline-none focus-visible:shadow-v25-focus ' +
        (activo
          ? 'border-v25-brand-600 bg-v25-brand-600/5 ring-1 ring-v25-brand-600 text-v25-ink-900'
          : 'border-v25-line bg-white text-v25-ink-700 hover:border-v25-line-strong hover:bg-v25-surface-sunken')
      }
    >
      <span className={'mt-0.5 ' + (activo ? 'text-v25-brand-600' : 'text-v25-ink-400')}>{icon}</span>
      <span className="min-w-0 flex-1">
        <span className="block text-sm font-medium">{label}</span>
        <span className="block text-xs text-v25-ink-500">{descripcion}</span>
      </span>
      <span
        aria-hidden="true"
        className={'mt-0.5 h-4 w-4 shrink-0 rounded-full border-2 ' + (activo ? 'border-v25-brand-600 bg-v25-brand-600 shadow-[inset_0_0_0_2px_#fff]' : 'border-v25-line-strong bg-white')}
      />
    </button>
  );
}

function Campo({ label, hint, error, children }) {
  return (
    <div>
      <label className="block text-xs font-medium text-slate-700 mb-1">{label}</label>
      {children}
      {error ? (
        <p className="text-xs text-red-600 mt-1">{error}</p>
      ) : hint ? (
        <p className="text-xs text-slate-400 mt-1">{hint}</p>
      ) : null}
    </div>
  );
}

function ConfirmacionCambioAmbito({
  ambitoActual,
  ambitoNuevo,
  cantidadAfectada,
  onConfirmar,
  onCancelar,
}) {
  const nombreAmbito = (a) => (a === 'interno' ? 'Internos' : 'Colaboradores');

  return (
    <div
      className="fixed inset-0 bg-slate-900/40 z-50 grid place-items-center px-4"
      onClick={onCancelar}
    >
      <div
        className="bg-white rounded-lg shadow-lg max-w-md w-full p-5"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start gap-3 mb-3">
          <AlertTriangle className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />
          <div>
            <h3 className="text-sm font-semibold text-slate-900">
              Cambiar ámbito de la campaña
            </h3>
            <p className="text-sm text-slate-500 mt-1">
              Vas a cambiar de <strong>{nombreAmbito(ambitoActual)}</strong> a{' '}
              <strong>{nombreAmbito(ambitoNuevo)}</strong>.
            </p>
          </div>
        </div>

        <p className="text-sm text-slate-700 bg-amber-50 border border-amber-100 rounded-md p-3 mb-4">
          Las {cantidadAfectada} {cantidadAfectada === 1 ? 'persona ya seleccionada' : 'personas ya seleccionadas'}{' '}
          se quitarán de la campaña: pertenecen al ámbito actual y no son válidas en
          el nuevo. Tendrás que volver a seleccionar técnicos y usuarios.
        </p>

        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancelar}
            className="px-3 py-1.5 text-sm rounded-lg border border-slate-200 hover:bg-slate-50"
          >
            Cancelar
          </button>
          <Button type="button" size="sm" onClick={onConfirmar}>
            Cambiar ámbito
          </Button>
        </div>
      </div>
    </div>
  );
}
